import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import './assets/scss/bottom.scss'
import './assets/scss/color.scss'
import './assets/scss/common.scss'
import './assets/scss/customize-antd.scss'
import './assets/scss/heading.scss'
import './assets/scss/icon.scss'
import './assets/scss/noti.scss'
import './assets/scss/position.scss'
import './assets/scss/sizing.scss'
import './assets/scss/spacing.scss'
import './features/stock/modal/type/attendu/Attendu.scss'
import './features/stock/attendu/Attendu.scss'
import './features/stock/modal/CustomModal.scss'
import { NotFound } from './components/Common'
import { CommandeType } from './enum/CommandeType'
import { MODE } from './enum/mode'
import { ScreenType } from './enum/ScreenType'

import usePressTab from './hook/usePressTab'
import { lazy, ReactNode, Suspense } from 'react'
import { Spin } from 'antd'
import { NavBar } from './components/Common/Nav/Nav'
import usePermissions from './hook/usePermissions'
import { showPermissionError } from './utils/notification'
import ErrorBoundary from './components/Common/ErrorBoundary'
import AuthGuard from './guard/AuthGuard'
import HomeLayout from './components/Layout/HomeLayout'
import { t } from 'i18next'
import { isSuperAdmin } from './utils/permissions'

const lazyRetry: any = function (componentImport: any) {
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem('retry-lazy-refreshed') || 'false'
    )
    // try to import the component
    componentImport()
      .then((component: ReactNode) => {
        window.sessionStorage.setItem('retry-lazy-refreshed', 'false') // success so reset the refresh
        resolve(component)
      })
      .catch((error: any) => {
        if (!hasRefreshed) {
          // not been refreshed yet
          window.sessionStorage.setItem('retry-lazy-refreshed', 'true') // we are now going to refresh
          return window.location.reload() // refresh the page
        }
        reject(error) // Default error behaviour as already tried refresh
      })
  })
}

const CommandeHistory = lazy(() =>
  lazyRetry(() => import('./features/history/commande/CommandeHistory'))
)

const HistoryGeneral = lazy(() =>
  lazyRetry(() => import('./features/history/general/HistoryGeneral'))
)
const AttenduHistory = lazy(() =>
  lazyRetry(() => import('./features/history/attendu/AttenduHistory'))
)
const SsccHistory = lazy(() =>
  lazyRetry(() => import('./features/history/sscc/SsccHistory'))
)
const StockCreate = lazy(() =>
  lazyRetry(
    () => import('./features/stock/stockscreen/component/modal/StockCreate')
  )
)
const TransporteurView = lazy(() =>
  lazyRetry(
    () => import('./features/stock/modal/type/commonModal/TransporteurViewOnly')
  )
)
const FournisseurView = lazy(() =>
  lazyRetry(
    () => import('./features/stock/modal/type/commonModal/FournisseurViewOnly')
  )
)
const DestinataireView = lazy(() =>
  lazyRetry(
    () => import('./features/stock/modal/type/commonModal/DestinataireViewOnly')
  )
)
const TestPdf = lazy(() =>
  lazyRetry(() => import('./features/stock/modal/type/commande/TestPdf'))
)
const CommandeMisEnPreparation = lazy(() =>
  lazyRetry(
    () =>
      import('./features/stock/modal/type/commande/CommandeMisEnPreparation')
  )
)
const CommandeEdit = lazy(() =>
  lazyRetry(() => import('./features/stock/modal/type/commande/CommandeEdit'))
)
const AttenduEdit = lazy(() =>
  lazyRetry(() => import('./features/stock/modal/type/attendu/AttenduEdit'))
)
const AttenduCreate = lazy(() =>
  lazyRetry(() => import('./features/stock/modal/type/attendu/AttenduCreate'))
)

const AttenduOnMission = lazy(() =>
  lazyRetry(() => import('./features/stock/attendu/AttenduOnMission'))
)

const ReferenceHistory = lazy(() =>
  lazyRetry(() => import('./features/history/reference/ReferenceHistory'))
)

const UtilisateurScreen = lazy(() =>
  lazyRetry(() => import('./features/configurations/utilisateur/Utilisateur'))
)

const UtilisateurDetail = lazy(() =>
  lazyRetry(
    () =>
      import(
        './features/configurations/utilisateur/userDetail/UtilisateurDetail'
      )
  )
)
const Societe = lazy(() =>
  lazyRetry(
    () => import('./features/configurations/societe/societeDetail/Societe')
  )
)
const RefEdit = lazy(() =>
  lazyRetry(
    () => import('./features/configurations/reference/catalogue/RefEdit')
  )
)
const ClientScreen = lazy(() =>
  lazyRetry(() => import('./features/configurations/client/ClientScreen'))
)

const ClientCreate = lazy(() =>
  lazyRetry(
    () => import('./features/configurations/client/clientDetail/ClientCreate')
  )
)

const CommandeCreate = lazy(() =>
  lazyRetry(() => import('./features/stock/modal/type/commande/CommandeCreate'))
)

const DashboardComponent = lazy(() =>
  lazyRetry(() => import('./features/dashboard/Dashboard'))
)

const StockComponent = lazy(() =>
  lazyRetry(() => import('./features/stock/stockscreen/NewStock'))
)
const AttenduComponent = lazy(() =>
  lazyRetry(() => import('./features/stock/attendu/Attendu'))
)
const CommandeComponent = lazy(() =>
  lazyRetry(() => import('./features/stock/commande/Commande'))
)
const ReferenceComponent = lazy(() =>
  lazyRetry(() => import('./features/configurations/reference/ReferenceScreen'))
)

const SocieteComponent = lazy(() =>
  lazyRetry(() => import('./features/configurations/societe/SocieteScreen'))
)

const ConditionnementPopup = lazy(() =>
  lazyRetry(
    () =>
      import(
        './features/stock/stockscreen/component/modal/ConditionnementPopup'
      )
  )
)

const EmplacementPopup = lazy(() =>
  lazyRetry(
    () =>
      import('./features/stock/stockscreen/component/modal/EmplacementPopup')
  )
)

const ClientEdit = lazy(() =>
  lazyRetry(
    () => import('./features/configurations/client/clientDetail/ClientEdit')
  )
)

const MissionComponent = lazy(() =>
  lazyRetry(() => import('./features/stock/mission/Mission'))
)

const MissionDetailComponent = lazy(() =>
  lazyRetry(() => import('./features/stock/mission/MissionDetail'))
)
const NotificationScreenComponent = lazy(() =>
  lazyRetry(() => import('./features/notification/NotificationScreen'))
)

const PrintingScreenComponent = lazy(() =>
  lazyRetry(() => import('./features/printing/PrintingScreen'))
)

const TiersScreenComponent = lazy(() =>
  lazyRetry(() => import('./features/configurations/tier/Tiers'))
)

const PrinterListComponent = lazy(() =>
  lazyRetry(() => import('./features/printers/PrinterList/PrinterList'))
)
const PrinterListTabComponent = lazy(() =>
  lazyRetry(
    () => import('./features/printers/PrinterList/components/PrinterListTab')
  )
)
const PrinterPoliciesTabComponent = lazy(() =>
  lazyRetry(
    () =>
      import('./features/printers/PrinterList/components/PrinterPoliciesTab')
  )
)
const PrinterDetailComponent = lazy(() =>
  lazyRetry(() => import('./features/printers/PrinterDetail/PrinterDetail'))
)
const PrinterPolicyDetailComponent = lazy(() =>
  lazyRetry(
    () => import('./features/printers/PrinterPolicyDetail/PrinterPolicyDetail')
  )
)

const FournisseurScreenComponent = lazy(() =>
  lazyRetry(() => import('./features/configurations/tier/Fournisseurs'))
)
const TransporteurScreenComponent = lazy(() =>
  lazyRetry(() => import('./features/configurations/tier/Transporteurs'))
)

const DestinataireScreenComponent = lazy(() =>
  lazyRetry(() => import('./features/configurations/tier/Destinataires'))
)

const FournisseurDetail = lazy(() =>
  lazyRetry(() => import('./features/configurations/tier/FournisseurDetail'))
)

const TransporteurDetail = lazy(() =>
  lazyRetry(() => import('./features/configurations/tier/TransporteurDetail'))
)

const DestinataireDetail = lazy(() =>
  lazyRetry(() => import('./features/configurations/tier/DestinataireDetail'))
)

const TableScreen = lazy(() =>
  lazyRetry(() => import('./features/tables/TablesDatabase'))
)

const ProtectedRoute = ({ children, tabName, ...rest }: any) => (
  <HomeLayout tabName={tabName} {...rest}>
    {children}
  </HomeLayout>
)

export const PermissionRoute = ({
  hasPermission,
}: {
  hasPermission: boolean
}) => {
  if (!hasPermission) {
    showPermissionError()
    return <Navigate to={'/tableau-de-bord'} />
  }
  return <Outlet />
}

export const PermissionPrinterRoute = () => {
  if (!isSuperAdmin()) {
    showPermissionError()
    return <Navigate to={'/tableau-de-bord'} />
  }
  return <PrinterListComponent />
}

function App() {
  usePressTab()
  const {
    canAccessStock,
    canAccessAttendu,
    canAccessCommande,
    canAccessMission,
    canAccessHistory,
    canAccessClient,
    canAccessUser,
    canAccessReference,
    canAccessCompany,
    canAccessBilling,
    canAccessInventory,
    canAccessParametres,
  } = usePermissions()

  return (
    <div>
      <div className="fixed top-0 w-full z-50">
        <NavBar />
      </div>
      <AuthGuard>
        <ErrorBoundary>
          <Suspense
            fallback={
              <div className="w-screen h-screen flex justify-center items-center">
                <Spin size="large" />
              </div>
            }
          >
            <Routes>
              <Route
                path="*"
                element={
                  <ProtectedRoute>
                    <NotFound />
                  </ProtectedRoute>
                }
              />

              {/* dashboard routes */}
              <Route
                path="/"
                element={<Navigate to={'/tableau-de-bord'} replace />}
              ></Route>
              <Route
                path="tableau-de-bord"
                element={
                  <ProtectedRoute tabName={t('title.dashboard')}>
                    <DashboardComponent />
                  </ProtectedRoute>
                }
              />

              {/* stock routes */}
              <Route
                path="/gestion-de-stock/stock"
                element={<PermissionRoute hasPermission={canAccessStock} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.stock')}>
                      <StockComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="chrono/reference/:id"
                  element={<ReferenceHistory />}
                />
                <Route path="chrono/sscc/:sscc" element={<SsccHistory />} />
                <Route
                  path="stock-create"
                  element={
                    <ProtectedRoute tabName={t('title.stock')}>
                      <StockCreate />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="conditionnement"
                  element={<ConditionnementPopup />}
                />
                <Route path="emplacement" element={<EmplacementPopup />} />
              </Route>

              {/* attendu routes */}
              <Route
                path="gestion-de-stock/attendu"
                element={<PermissionRoute hasPermission={canAccessAttendu} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.attendus')}>
                      <AttenduComponent />
                    </ProtectedRoute>
                  }
                />
                <Route path="chrono/sscc/:sscc" element={<SsccHistory />} />
                <Route path="attendu-create" element={<AttenduCreate />} />
                <Route
                  path="attendu-on-mission/:id"
                  element={
                    <ProtectedRoute>
                      <AttenduOnMission />
                    </ProtectedRoute>
                  }
                />
                <Route path="attendu-edit/:id" element={<AttenduEdit />} />

                <Route
                  path="fournisseur/only-view/:id"
                  element={<FournisseurView />}
                />
                <Route path="chrono/:id" element={<AttenduHistory />} />
              </Route>
              <Route
                path="reference/only-view/:id"
                element={<RefEdit mode={MODE.VIEW} />}
              />
              {/* commande routes */}
              <Route
                path="gestion-de-stock/commande"
                element={<PermissionRoute hasPermission={canAccessCommande} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.commandes')}>
                      <CommandeComponent />
                    </ProtectedRoute>
                  }
                />
                <Route path="chrono/sscc/:sscc" element={<SsccHistory />} />
                <Route path="commande-create" element={<CommandeCreate />} />
                <Route path="commande-edit/:id" element={<CommandeEdit />} />
                <Route
                  path="commande-preparation/:id"
                  element={
                    <CommandeMisEnPreparation
                      commandeType={CommandeType.Mis_en_preparation}
                    />
                  }
                />
                <Route
                  path="commande-envoye-en-mission/:id"
                  element={
                    <CommandeMisEnPreparation
                      commandeType={CommandeType.Envoye_en_mission}
                    />
                  }
                />
                <Route
                  path="commande-envoyer-en-chargement/:id"
                  element={
                    <CommandeMisEnPreparation
                      commandeType={CommandeType.Envoyer_en_chargement}
                    />
                  }
                />
                <Route
                  path="destinataire/only-view/:id/:id_contact/:id_add"
                  element={<DestinataireView />}
                />
                <Route
                  path="transporteur/only-view/:id/:id_add/:id_contact"
                  element={<TransporteurView />}
                />
                <Route path="chrono/:id" element={<CommandeHistory />} />
              </Route>

              {/* mission routes */}
              <Route
                path="gestion-de-stock/mission"
                element={<PermissionRoute hasPermission={canAccessMission} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.missions')}>
                      <MissionComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="mission-detail"
                  element={
                    <MissionDetailComponent screenType={ScreenType.popup} />
                  }
                />
                <Route
                  path="attendu-on-mission/:id"
                  element={<AttenduOnMission />}
                />

                {/*show Command popup detail in Mission*/}
                <Route path="commande-edit/:id" element={<CommandeEdit />} />
                <Route
                  path="commande-preparation/:id"
                  element={
                    <CommandeMisEnPreparation
                      commandeType={CommandeType.Mis_en_preparation}
                    />
                  }
                />
                <Route
                  path="commande-envoye-en-mission/:id"
                  element={
                    <CommandeMisEnPreparation
                      commandeType={CommandeType.Envoye_en_mission}
                    />
                  }
                />
                <Route
                  path="commande-envoyer-en-chargement/:id"
                  element={
                    <CommandeMisEnPreparation
                      commandeType={CommandeType.Envoyer_en_chargement}
                    />
                  }
                />
              </Route>

              {/* history routes */}
              <Route
                path="gestion-de-stock/history"
                element={<PermissionRoute hasPermission={canAccessHistory} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.history')}>
                      <HistoryGeneral />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* configurations routes */}
              {/* reference routes */}
              <Route
                path="/configurations/reference"
                element={<PermissionRoute hasPermission={canAccessReference} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.reference')}>
                      <ReferenceComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute>
                      <RefEdit mode={MODE.EDIT} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="ref-create"
                  element={
                    <ProtectedRoute>
                      <RefEdit mode={MODE.CREATE} />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* societe route */}
              <Route
                path="/configurations/societe"
                element={<PermissionRoute hasPermission={canAccessCompany} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.companies')}>
                      <SocieteComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="societe-create"
                  element={
                    <ProtectedRoute>
                      <Societe mode={MODE.CREATE} />
                    </ProtectedRoute>
                  }
                ></Route>
                <Route
                  path="societe-edit"
                  element={
                    <ProtectedRoute>
                      <Societe mode={MODE.EDIT} />
                    </ProtectedRoute>
                  }
                ></Route>
              </Route>

              {/* client routes */}
              <Route
                path="configurations/client"
                element={<PermissionRoute hasPermission={canAccessClient} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.clients')}>
                      <ClientScreen />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute>
                      <ClientEdit />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="client-create"
                  element={
                    <ProtectedRoute>
                      <ClientCreate />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* utilisateur routes */}
              <Route
                path="/configurations/utilisateur"
                element={<PermissionRoute hasPermission={canAccessUser} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.users')}>
                      <UtilisateurScreen />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="utilisateur-create"
                  element={
                    <ProtectedRoute>
                      <UtilisateurDetail mode={MODE.CREATE} />
                    </ProtectedRoute>
                  }
                ></Route>
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute>
                      <UtilisateurDetail mode={MODE.EDIT} />
                    </ProtectedRoute>
                  }
                ></Route>
              </Route>
              <Route
                path="/configurations/tiers"
                element={<PermissionRoute hasPermission />}
              >
                <Route
                  element={
                    <ProtectedRoute>
                      <TiersScreenComponent />
                    </ProtectedRoute>
                  }
                >
                  <Route
                    index
                    element={<Navigate replace to="fournisseurs" />}
                  />
                  <Route
                    path="fournisseurs"
                    element={
                      <ProtectedRoute tabName={t('title.partners.suppliers')}>
                        <FournisseurScreenComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="transporteurs"
                    element={
                      <ProtectedRoute tabName={t('title.partners.carriers')}>
                        <TransporteurScreenComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="destinataires"
                    element={
                      <ProtectedRoute tabName={t('title.partners.recipients')}>
                        <DestinataireScreenComponent />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="fournisseurs/create"
                    element={<FournisseurDetail />}
                  />
                  <Route
                    path="fournisseurs/entity/:id"
                    element={<FournisseurDetail />}
                  />
                  <Route
                    path="transporteurs/create"
                    element={<TransporteurDetail />}
                  />
                  <Route
                    path="transporteurs/entity/:id"
                    element={<TransporteurDetail />}
                  />
                  <Route
                    path="destinataires/create"
                    element={<DestinataireDetail />}
                  />
                  <Route
                    path="destinataires/entity/:id"
                    element={<DestinataireDetail />}
                  />
                </Route>
              </Route>

              {/* Printer routes */}
              <Route path="/printers" element={<PermissionPrinterRoute />}>
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <PrinterListTabComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path=":id"
                  element={
                    <ProtectedRoute>
                      <PrinterDetailComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="policies"
                  element={
                    <ProtectedRoute>
                      <PrinterPoliciesTabComponent />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="policies/create"
                  element={
                    <ProtectedRoute>
                      <PrinterPolicyDetailComponent mode={MODE.CREATE} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="policies/:id"
                  element={
                    <ProtectedRoute>
                      <PrinterPolicyDetailComponent mode={MODE.EDIT} />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route
                path="/printing"
                element={
                  <ProtectedRoute>
                    <PrintingScreenComponent />
                  </ProtectedRoute>
                }
              ></Route>

              {/* facturation routes */}
              <Route
                path="/facturation"
                element={<PermissionRoute hasPermission={canAccessBilling} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.billing')}>
                      <NotFound />
                    </ProtectedRoute>
                  }
                />
              </Route>

              {/* inventaire routes */}
              <Route
                path="/inventaire"
                element={<PermissionRoute hasPermission={canAccessInventory} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.inventory')}>
                      <NotFound />
                    </ProtectedRoute>
                  }
                />
              </Route>

              <Route
                path="/test/file/123"
                element={
                  <ProtectedRoute>
                    <TestPdf />
                  </ProtectedRoute>
                }
              ></Route>
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute tabName={t('title.notifications')}>
                    <NotificationScreenComponent />
                  </ProtectedRoute>
                }
              ></Route>

              {/* Table Database route */}
              <Route
                path="/tables"
                element={<PermissionRoute hasPermission={isSuperAdmin()} />}
              >
                <Route
                  index
                  element={
                    <ProtectedRoute tabName={t('title.database')}>
                      <TableScreen />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Routes>
          </Suspense>
        </ErrorBoundary>
      </AuthGuard>
    </div>
  )
}

export default App
